import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'adhd_custom_templates';

/**
 * Hook pour gérer les templates personnalisés
 * Permet de sauvegarder, charger et supprimer des templates de semaine
 */
export function useCustomTemplates() {
  const [templates, setTemplates] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Sauvegarder dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  }, [templates]);

  /**
   * Sauvegarder la semaine actuelle comme template
   * @param {string} name - Nom du template
   * @param {Object} weekTasks - Tâches de la semaine { dateStr: tasks[] }
   * @param {Object} options - Options supplémentaires (description, icon, etc.)
   */
  const saveTemplate = useCallback((name, weekTasks, options = {}) => {
    // Normaliser les tâches (enlever les dates spécifiques, garder juste jour de semaine)
    const normalizedTasks = {};

    Object.entries(weekTasks).forEach(([dateStr, tasks]) => {
      const date = new Date(dateStr);
      const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0=Lundi, 6=Dimanche

      normalizedTasks[dayIndex] = tasks.map(task => ({
        task: task.task,
        time: task.time,
        duration: task.duration,
        category: task.category,
        iconName: task.iconName,
        instructions: task.instructions || [],
        points: task.points || 10
      }));
    });

    const newTemplate = {
      id: `template_${Date.now()}`,
      name,
      description: options.description || '',
      icon: options.icon || '📋',
      color: options.color || 'zinc',
      tasks: normalizedTasks,
      taskCount: Object.values(normalizedTasks).flat().length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setTemplates(prev => [...prev, newTemplate]);
    return newTemplate;
  }, []);

  /**
   * Mettre à jour un template existant
   */
  const updateTemplate = useCallback((templateId, updates) => {
    setTemplates(prev => prev.map(t =>
      t.id === templateId
        ? { ...t, ...updates, updatedAt: new Date().toISOString() }
        : t
    ));
  }, []);

  /**
   * Supprimer un template
   */
  const deleteTemplate = useCallback((templateId) => {
    setTemplates(prev => prev.filter(t => t.id !== templateId));
  }, []);

  /**
   * Appliquer un template pour une semaine donnée
   * @param {string} templateId - ID du template
   * @param {Date} startDate - Date de début de la semaine
   * @returns {Object} - Tâches générées { dateStr: tasks[] }
   */
  const applyTemplate = useCallback((templateId, startDate) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return null;

    const weekTasks = {};

    // Calculer le lundi de la semaine
    const start = new Date(startDate);
    const dayOfWeek = start.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    start.setDate(start.getDate() + mondayOffset);

    // Générer les tâches pour chaque jour
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(start);
      currentDate.setDate(start.getDate() + i);
      const dateStr = currentDate.toISOString().split('T')[0];

      const dayTasks = template.tasks[i] || [];
      weekTasks[dateStr] = dayTasks.map(task => ({
        ...task,
        id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        date: dateStr,
        isCustom: true,
        isGenerated: true,
        templateId: template.id
      }));
    }

    return weekTasks;
  }, [templates]);

  /**
   * Dupliquer un template
   */
  const duplicateTemplate = useCallback((templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return null;

    const newTemplate = {
      ...template,
      id: `template_${Date.now()}`,
      name: `${template.name} (copie)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setTemplates(prev => [...prev, newTemplate]);
    return newTemplate;
  }, [templates]);

  return {
    templates,
    saveTemplate,
    updateTemplate,
    deleteTemplate,
    applyTemplate,
    duplicateTemplate
  };
}

export default useCustomTemplates;
